import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';

const NAV_LINKS = [
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/system-map', label: 'System Map' },
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/blog', label: 'Blog' },
  { to: '/about', label: 'About' },
];

// Shared header for every public marketing page (homepage, features, pricing,
// about, contact, etc.). Rendered once by AppLayout for every route in its
// isMarketingPage list -- extracted from HomePage.tsx's own inline header,
// which was the only marketing page that had one. Every other marketing
// page (About, Pricing, Contact, Team, Privacy, Cookies, Accessibility,
// Blog, How It Works, Why Us, ...) had none, so once AppLayout stopped
// rendering the internal admin Header on marketing routes, those pages had
// no header/logo/nav at all. This is the one place that gap gets fixed.
export const MarketingHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="!sticky top-0 z-50 border-b border-white/10 bg-[#020617]/80 backdrop-blur-xl">
      <div className="container mx-auto px-4 max-w-6xl h-16 flex items-center justify-between">
        <Link to="/homepage"><BrandLogo size="md" /></Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-white/60">
          {NAV_LINKS.map(link => (
            <Link key={link.to} to={link.to} className="hover:text-white transition-colors">{link.label}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="hidden sm:inline text-sm font-medium text-white/60 hover:text-white transition-colors">Log in</Link>
          <Link to="/signup" className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#3B82F6] to-[#A855F7] text-white text-sm font-bold hover:opacity-90 transition-all">
            Get Started
          </Link>
          {/* Mobile menu toggle -- below md, NAV_LINKS and the Log-in link above
              are both hidden with no other way to reach them, so this is the
              only path to How It Works/Features/Pricing/Blog/About/Log in
              on a phone. */}
          <button
            type="button"
            onClick={() => setMenuOpen(o => !o)}
            className="md:hidden p-2 -mr-2 text-white/70 hover:text-white transition-colors"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="md:hidden border-t border-white/10 bg-[#020617]/95 backdrop-blur-xl px-4 py-3 flex flex-col gap-1">
          {NAV_LINKS.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className="px-2 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/login"
            onClick={() => setMenuOpen(false)}
            className="px-2 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-colors"
          >
            Log in
          </Link>
        </nav>
      )}
    </header>
  );
};
